import { useMantineTheme } from '@mantine/core';
import * as React from 'react';

import { toPixels } from '@/utils/toPixels';

/** Ширина ≥ этого значения считается десктопом; всё что меньше — компактный UI (телефон или планшет). Согласовано с SCSS `@include compactViewport` (max-width: 1023px). */
export const DESKTOP_MIN_WIDTH_PX = 1024;

export type Devices = 'desktop' | 'tablet' | 'phone';

const Context = React.createContext<Devices>('desktop');

type DeviceProviderProps = {
  children?: React.ReactNode;
};

export const DeviceProvider: React.FC<DeviceProviderProps> = ({ children }) => {
  const theme = useMantineTheme();

  const phonePx = toPixels(theme.breakpoints.sm);

  const getCurrentDevice = React.useCallback(() => {
    const width = window.innerWidth;

    if (width >= DESKTOP_MIN_WIDTH_PX) {
      return 'desktop';
    }

    if (width <= phonePx) {
      return 'phone';
    }

    return 'tablet';
  }, [phonePx]);

  const [device, setDevice] = React.useState<Devices>(getCurrentDevice());

  React.useEffect(() => {
    const handleResize = (): void => {
      setDevice(getCurrentDevice());
    };

    window.addEventListener('resize', handleResize);

    return (): void => {
      window.removeEventListener('resize', handleResize);
    };
  }, [getCurrentDevice]);

  return <Context.Provider value={device}>{children}</Context.Provider>;
};

export const useDevices = (): {
  device: Devices;
  isPhone: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  /** Телефон и планшет: компактный UI при ширине окна меньше {@link DESKTOP_MIN_WIDTH_PX}px. */
  isCompactViewport: boolean;
} => {
  const currentDevice = React.useContext(Context);

  return React.useMemo(
    () => ({
      device: currentDevice,
      isPhone: currentDevice === 'phone',
      isTablet: currentDevice === 'tablet',
      isDesktop: currentDevice === 'desktop',
      isCompactViewport: currentDevice !== 'desktop',
    }),
    [currentDevice],
  );
};
